/**
 * dm.policy REST wire (W3-B P1 doors) — vocabularies and readers. Dependency-free but for the
 * shared vocabulary LEAF, imported by its subpath (the launcher table imports the route
 * through `dmPoliciesUrl`; the shared root barrel stays out of that chain).
 *
 * The closed vocabularies are the api's own (`@elmes/shared/pm/dmPolicyVocabulary`, DM #2382).
 * Stored values are published as written (lenient read), so every reader narrows.
 */
import {
  DM_POLICY_ENFORCEMENT_VALUES,
  DM_POLICY_KEY_MAX,
  DM_POLICY_KEY_PATTERN,
  type DmPolicyEnforcement,
  type DmPolicyRole,
} from '@elmes/shared/pm/dmPolicyVocabulary';

export {
  DM_POLICY_APPLIES_TO_ALL,
  DM_POLICY_APPLIES_TO_PSEUDO,
  DM_POLICY_APPLIES_TO_SUBAGENTS,
  DM_POLICY_CHECK_TYPE_VALUES,
  DM_POLICY_ENFORCEMENT_VALUES,
  DM_POLICY_GATE_NAMES,
  DM_POLICY_HOOK_DECISION_VALUES,
  DM_POLICY_HOOK_EVENT,
  DM_POLICY_HOOK_TOOL_VALUES,
  DM_POLICY_KERNEL_NAMES,
  DM_POLICY_KEY_MAX,
  DM_POLICY_KEY_PATTERN,
  DM_POLICY_KIND_VALUES,
  DM_POLICY_ROLE_VALUES,
  DM_POLICY_STATUS_VALUES,
  type DmPolicyEnforcement,
  type DmPolicyKind,
  type DmPolicyRole,
  type DmPolicyStatus,
} from '@elmes/shared/pm/dmPolicyVocabulary';

export const DM_POLICIES_PATH = '/dm/policies';
export const DM_POLICY_BINDINGS_PATH = '/dm/policy-bindings';

export function isDmPolicyKey(raw: string): boolean {
  return raw.length <= DM_POLICY_KEY_MAX && DM_POLICY_KEY_PATTERN.test(raw);
}

export interface DmPolicyCheck {
  type: string;
  event: string | null;
  tool: string | null;
  match: string | null;
  message: string | null;
  gate: string | null;
  /** Hook verdict as stored (`null` = deny). */
  decision: string | null;
  /** Kernel check enforcer. */
  enforcedBy: string | null;
}

export interface DmPolicyRow {
  itemId: number;
  itemNo: number;
  title: string;
  status: string;
  key: string | null;
  kind: string | null;
  enforcement: string | null;
  appliesTo: string[] | null;
  check: DmPolicyCheck | null;
  summary: string | null;
  version: number | null;
  sourceRef: string | null;
  manualReason: string | null;
  /** Stored fields the current contract does not admit (count only is rendered). */
  diagnosticCount: number;
}

export interface DmPolicyDetail {
  policy: DmPolicyRow & { body: string | null };
  supersedes: number[];
  supersededBy: number[];
  activationProblems: string[];
}

export interface DmPolicyListPage {
  rows: DmPolicyRow[];
  hasMore: boolean;
  /** Rows the door sent, dropped ones included — the paging cursor's step. */
  receivedCount: number;
}

export interface DmPolicyBindingRow {
  bindingId: number;
  policyItemId: number;
  mode: string;
  targetKind: string;
  targetProjectId: number | null;
  targetItemId: number | null;
  targetRunId: number | null;
  targetAgentKey: string | null;
  reason: string | null;
}

export interface DmPolicyBindingPage {
  rows: DmPolicyBindingRow[];
  hasMore: boolean;
}

type Bag = Record<string, unknown>;

function bagOf(raw: unknown): Bag | null {
  return typeof raw === 'object' && raw !== null && !Array.isArray(raw) ? (raw as Bag) : null;
}

/** `res.data.data` of the platform envelope. */
function envelopeData(res: unknown): Bag {
  return bagOf((res as { data?: { data?: unknown } } | undefined)?.data?.data) ?? {};
}

function str(raw: unknown): string | null {
  return typeof raw === 'string' && raw !== '' ? raw : null;
}

function int(raw: unknown): number | null {
  return typeof raw === 'number' && Number.isInteger(raw) ? raw : null;
}

function positiveInts(raw: unknown): number[] {
  return Array.isArray(raw) ? raw.filter((v): v is number => int(v) !== null && v > 0) : [];
}

function readCheck(raw: unknown): DmPolicyCheck | null {
  const bag = bagOf(raw);
  const type = bag === null ? null : str(bag.type);
  if (bag === null || type === null) return null;
  return {
    type,
    event: str(bag.event),
    tool: str(bag.tool),
    match: str(bag.match),
    message: str(bag.message),
    gate: str(bag.gate),
    decision: str(bag.decision),
    enforcedBy: str(bag.enforced_by),
  };
}

function readRow(raw: unknown): DmPolicyRow | null {
  const bag = bagOf(raw);
  if (bag === null) return null;
  const itemId = int(bag.itemId);
  const itemNo = int(bag.itemNo);
  if (itemId === null || itemNo === null || itemId <= 0 || itemNo <= 0) return null;
  const appliesTo = Array.isArray(bag.applies_to)
    ? bag.applies_to.filter((v): v is string => typeof v === 'string')
    : null;
  return {
    itemId,
    itemNo,
    title: typeof bag.title === 'string' ? bag.title : `#${itemNo}`,
    status: typeof bag.status === 'string' ? bag.status : '',
    key: str(bag.key),
    kind: str(bag.kind),
    enforcement: str(bag.enforcement),
    appliesTo,
    check: readCheck(bag.check),
    summary: str(bag.summary),
    version: int(bag.version),
    sourceRef: str(bag.source_ref),
    manualReason: str(bag.manual_reason),
    diagnosticCount: Array.isArray(bag.diagnostics) ? bag.diagnostics.length : 0,
  };
}

function rowsOf<T>(raw: unknown, read: (entry: unknown) => T | null): T[] {
  return Array.isArray(raw)
    ? raw.flatMap((entry) => {
        const row = read(entry);
        return row === null ? [] : [row];
      })
    : [];
}

export function readDmPolicyListPage(res: unknown): DmPolicyListPage {
  const data = envelopeData(res);
  return {
    rows: rowsOf(data.rows, readRow),
    hasMore: data.hasMore === true,
    receivedCount: Array.isArray(data.rows) ? data.rows.length : 0,
  };
}

/** `null` when the answer carries no readable policy. */
export function readDmPolicyDetail(res: unknown): DmPolicyDetail | null {
  const data = envelopeData(res);
  const policyBag = bagOf(data.policy);
  const row = readRow(policyBag);
  if (row === null || policyBag === null) return null;
  return {
    policy: { ...row, body: str(policyBag.body) },
    supersedes: positiveInts(data.supersedes),
    supersededBy: positiveInts(data.supersededBy),
    activationProblems: Array.isArray(data.activationProblems)
      ? data.activationProblems.filter((v): v is string => typeof v === 'string')
      : [],
  };
}

function readBinding(raw: unknown): DmPolicyBindingRow | null {
  const bag = bagOf(raw);
  if (bag === null) return null;
  const bindingId = int(bag.bindingId);
  const policyItemId = int(bag.policyItemId);
  const targetKind = str(bag.targetKind);
  if (bindingId === null || policyItemId === null || targetKind === null) return null;
  return {
    bindingId,
    policyItemId,
    mode: typeof bag.mode === 'string' ? bag.mode : '',
    targetKind,
    targetProjectId: int(bag.targetProjectId),
    targetItemId: int(bag.targetItemId),
    targetRunId: int(bag.targetRunId),
    targetAgentKey: str(bag.targetAgentKey),
    reason: str(bag.reason),
  };
}

export function readDmPolicyBindingPage(res: unknown): DmPolicyBindingPage {
  const data = envelopeData(res);
  return { rows: rowsOf(data.rows, readBinding), hasMore: data.hasMore === true };
}

/** One binding target as text; pm_item ids print as `itemId=N` (they are not item numbers). */
export function dmPolicyBindingTargetLabel(
  b: Pick<DmPolicyBindingRow, 'targetKind' | 'targetProjectId' | 'targetItemId' | 'targetRunId' | 'targetAgentKey'>,
): string {
  switch (b.targetKind) {
    case 'tenant':
      return 'tenant';
    case 'project':
      return `project ${b.targetProjectId ?? '?'}`;
    case 'item':
      return `itemId=${b.targetItemId ?? '?'}`;
    case 'run':
      return `run ${b.targetRunId ?? '?'}`;
    case 'agent':
      return `agent ${b.targetAgentKey ?? '?'}`;
    default:
      return b.targetKind;
  }
}

// ── binding writes (W3-B UIb) — mirror `dmPolicyBinding.service.ts` + `pmPolicyBinding.ts` ──

export const DM_POLICY_BINDING_TARGET_KIND_VALUES = ['tenant', 'project', 'item', 'agent', 'run'] as const;
export const DM_POLICY_BINDING_MODE_VALUES = ['apply', 'exempt'] as const;
export const DM_POLICY_BINDING_REASON_MAX = 1000;

export type DmPolicyBindingTargetKind = (typeof DM_POLICY_BINDING_TARGET_KIND_VALUES)[number];
export type DmPolicyBindingMode = (typeof DM_POLICY_BINDING_MODE_VALUES)[number];
export type DmPolicyBindingCoordinate = 'targetProjectId' | 'targetItemId' | 'targetRunId' | 'targetAgentKey';

/** Each kind's one coordinate (the api's `COORDINATE_OF`); a tenant target has none. */
export const DM_POLICY_BINDING_COORDINATE_OF: Readonly<Record<DmPolicyBindingTargetKind, DmPolicyBindingCoordinate | null>> = {
  tenant: null,
  project: 'targetProjectId',
  item: 'targetItemId',
  agent: 'targetAgentKey',
  run: 'targetRunId',
};

/** `POST /dm/policies/:id/bind` and `/exempt` body: the kind, its own coordinate, the reason. */
export interface DmPolicyBindingBody {
  targetKind: DmPolicyBindingTargetKind;
  targetProjectId?: number;
  targetItemId?: number;
  targetRunId?: number;
  targetAgentKey?: DmPolicyRole;
  reason?: string;
}

export function dmPolicyBindingWritePath(policyItemId: number, mode: DmPolicyBindingMode): string {
  return `${DM_POLICIES_PATH}/${policyItemId}/${mode === 'apply' ? 'bind' : 'exempt'}`;
}

/** Its body is `{}` (the door's schema is strict). */
export function dmPolicyUnbindPath(bindingId: number): string {
  return `${DM_POLICY_BINDINGS_PATH}/${bindingId}/unbind`;
}

// ── effective pack (W3-B UIb) — snake_case as `dmPolicyPack.service.ts` publishes it ──

export const DM_POLICY_PACK_PATH = '/dm/policy-pack';
/** S3 levels, most specific first (`DmPolicyLevelName`). */
export const DM_POLICY_LEVEL_VALUES = ['run', 'agent', 'sprint', 'item', 'ancestor', 'member', 'project', 'tenant'] as const;
/** The resolver's codes (`DmPolicyDiagnosticCode`); the pack adds `role_unresolved` / `role_unverified`. */
export const DM_POLICY_RESOLVER_DIAGNOSTIC_CODES = ['overridden_by_lock', 'exempt_refused_locked', 'conflict', 'unreadable'] as const;
/** `brief_ref.policy_pack` as the run detail door projects it (`DM_POLICY_SNAPSHOT_STATES`). */
export const DM_POLICY_SNAPSHOT_STATES = ['recorded', 'unavailable', 'not_recorded', 'withheld'] as const;

export type DmPolicyLevel = (typeof DM_POLICY_LEVEL_VALUES)[number];

export type DmPolicyLevelTargetWire =
  | { kind: 'tenant' }
  | { kind: 'project' | 'item' | 'run'; id: number }
  | { kind: 'agent'; agentKey: string };

export interface DmPolicyPackEntryWire {
  key: string;
  policy_item_id: number;
  version: number | null;
  level: DmPolicyLevel;
  target: DmPolicyLevelTargetWire;
  enforcement: DmPolicyEnforcement;
  kind: string | null;
  summary: string | null;
  check: DmPolicyCheck | null;
  applies_to: string[];
}

export interface DmPolicyPackExemptionWire {
  key: string;
  reason: string | null;
  by: string;
  level: DmPolicyLevel;
}

export interface DmPolicyPackDiagnosticWire {
  key: string | null;
  code: string;
  policy_item_id?: number;
}

export interface DmPolicyRatioWire {
  count: number;
  total: number;
  ratio: number | null;
}

export interface DmPolicyPackWire {
  resolved_at: string;
  context: { role: string | null; levels: Array<{ level: DmPolicyLevel; target: DmPolicyLevelTargetWire }> };
  entries: DmPolicyPackEntryWire[];
  exemptions: DmPolicyPackExemptionWire[];
  diagnostics: DmPolicyPackDiagnosticWire[];
  /** `null` when the stored value does not read — never a made-up ratio. */
  proseOnly: DmPolicyRatioWire | null;
  budget: DmPolicyPackBudgetWire | null;
}

/** `enforced` (CAPS T2 floor): `null` on a snapshot stored before it, or when its fields do not read. */
interface DmPolicyPackBudgetWire {
  fit: number;
  total: number;
  tokens: number;
  enforced: { fit: number; total: number; hard_tokens: number } | null;
}

/** The part of `DmPolicyReport` this surface reads: the D24 prose-only counter. */
export interface DmPolicyReportWire {
  proseOnly: DmPolicyRatioWire;
}

export interface DmPolicyPackRead {
  pack: DmPolicyPackWire;
  /** Stored entries/exemptions/diagnostics that did not narrow — rendered as a count, never hidden. */
  dropped: number;
}

function memberOfList<T extends string>(raw: unknown, values: readonly T[]): T | null {
  return typeof raw === 'string' && (values as readonly string[]).includes(raw) ? (raw as T) : null;
}

function readLevelTarget(raw: unknown): DmPolicyLevelTargetWire | null {
  const bag = bagOf(raw);
  if (bag === null) return null;
  if (bag.kind === 'tenant') return { kind: 'tenant' };
  if (bag.kind === 'agent') {
    const agentKey = str(bag.agentKey);
    return agentKey === null ? null : { kind: 'agent', agentKey };
  }
  const id = int(bag.id);
  if (id === null || id <= 0) return null;
  return bag.kind === 'project' || bag.kind === 'item' || bag.kind === 'run' ? { kind: bag.kind, id } : null;
}

function readRatio(raw: unknown): DmPolicyRatioWire | null {
  const bag = bagOf(raw);
  const count = bag === null ? null : int(bag.count);
  const total = bag === null ? null : int(bag.total);
  if (bag === null || count === null || total === null) return null;
  return { count, total, ratio: typeof bag.ratio === 'number' ? bag.ratio : null };
}

function readBudget(bag: Bag | null): DmPolicyPackWire['budget'] {
  const fit = bag === null ? null : int(bag.fit);
  const total = bag === null ? null : int(bag.total);
  const tokens = bag === null ? null : int(bag.tokens);
  if (bag === null || fit === null || total === null || tokens === null) return null;
  const enforcedFit = int(bag.enforced_fit);
  const enforcedTotal = int(bag.enforced_total);
  const hardTokens = int(bag.hard_tokens);
  const enforced =
    enforcedFit === null || enforcedTotal === null || hardTokens === null ? null : { fit: enforcedFit, total: enforcedTotal, hard_tokens: hardTokens };
  return { fit, total, tokens, enforced };
}

function readPackEntry(raw: unknown): DmPolicyPackEntryWire | null {
  const bag = bagOf(raw);
  if (bag === null) return null;
  const key = str(bag.key);
  const policyItemId = int(bag.policy_item_id);
  const level = memberOfList(bag.level, DM_POLICY_LEVEL_VALUES);
  const target = readLevelTarget(bag.target);
  const enforcement = memberOfList(bag.enforcement, DM_POLICY_ENFORCEMENT_VALUES);
  if (key === null || policyItemId === null || level === null || target === null || enforcement === null) return null;
  return {
    key,
    policy_item_id: policyItemId,
    version: int(bag.version),
    level,
    target,
    enforcement,
    kind: str(bag.kind),
    summary: str(bag.summary),
    check: readCheck(bag.check),
    applies_to: Array.isArray(bag.applies_to) ? bag.applies_to.filter((v): v is string => typeof v === 'string') : [],
  };
}

function readPackExemption(raw: unknown): DmPolicyPackExemptionWire | null {
  const bag = bagOf(raw);
  const key = bag === null ? null : str(bag.key);
  const level = bag === null ? null : memberOfList(bag.level, DM_POLICY_LEVEL_VALUES);
  if (bag === null || key === null || level === null) return null;
  return { key, reason: str(bag.reason), by: typeof bag.by === 'string' ? bag.by : '', level };
}

function readPackDiagnostic(raw: unknown): DmPolicyPackDiagnosticWire | null {
  const bag = bagOf(raw);
  const code = bag === null ? null : str(bag.code);
  if (bag === null || code === null) return null;
  const policyItemId = int(bag.policy_item_id);
  return { key: str(bag.key), code, ...(policyItemId === null ? {} : { policy_item_id: policyItemId }) };
}

/** A pack document (live answer's `pack` or a run's stored snapshot) → narrowed, or `null` without `entries`. */
export function readDmPolicyPack(raw: unknown): DmPolicyPackRead | null {
  const bag = bagOf(raw);
  if (bag === null || !Array.isArray(bag.entries)) return null;
  const lists = {
    entries: bag.entries,
    exemptions: Array.isArray(bag.exemptions) ? bag.exemptions : [],
    diagnostics: Array.isArray(bag.diagnostics) ? bag.diagnostics : [],
  };
  const entries = rowsOf(lists.entries, readPackEntry);
  const exemptions = rowsOf(lists.exemptions, readPackExemption);
  const diagnostics = rowsOf(lists.diagnostics, readPackDiagnostic);
  const context = bagOf(bag.context);
  const budget = bagOf(bag.budget);
  const levels = Array.isArray(context?.levels)
    ? context.levels.flatMap((l: unknown) => {
        const lb = bagOf(l);
        const level = lb === null ? null : memberOfList(lb.level, DM_POLICY_LEVEL_VALUES);
        const target = lb === null ? null : readLevelTarget(lb.target);
        return level === null || target === null ? [] : [{ level, target }];
      })
    : [];
  return {
    pack: {
      resolved_at: typeof bag.resolved_at === 'string' ? bag.resolved_at : '',
      context: { role: str(context?.role), levels },
      entries,
      exemptions,
      diagnostics,
      proseOnly: readRatio(bag.proseOnly),
      budget: readBudget(budget),
    },
    dropped:
      lists.entries.length - entries.length +
      (lists.exemptions.length - exemptions.length) +
      (lists.diagnostics.length - diagnostics.length),
  };
}

/** `GET /dm/policy-pack?report=true` → the pack and the D24 report (`null` when the answer has none). */
export function readDmPolicyPackAnswer(res: unknown): (DmPolicyPackRead & { report: DmPolicyReportWire | null }) | null {
  const data = envelopeData(res);
  const read = readDmPolicyPack(data.pack);
  if (read === null) return null;
  const proseOnly = readRatio(bagOf(data.report)?.proseOnly);
  return { ...read, report: proseOnly === null ? null : { proseOnly } };
}

/** A run's pack snapshot as read off the detail door; `pack` is narrowed separately. */
export type DmPolicySnapshotRead =
  | { state: 'recorded'; pack: DmPolicyPackRead | null }
  | { state: 'unavailable'; resolvedAt: string | null; reason: string }
  | { state: 'not_recorded' }
  | { state: 'withheld' }
  /** The member is absent or carries a state this build does not know. */
  | { state: 'unpublished' };

export function readDmPolicySnapshot(raw: unknown): DmPolicySnapshotRead {
  const bag = bagOf(raw);
  const state = bag === null ? null : memberOfList(bag.state, DM_POLICY_SNAPSHOT_STATES);
  if (bag === null || state === null) return { state: 'unpublished' };
  if (state === 'recorded') return { state, pack: readDmPolicyPack(bag.pack) };
  if (state === 'unavailable') {
    return { state, resolvedAt: str(bag.resolved_at), reason: typeof bag.reason === 'string' ? bag.reason : '' };
  }
  return { state };
}
